import { dialog } from "../../../../../nq-component";
import BaseFormPresenter from "../../../../../base/BaseFormPresenter";
import DialogModal from "../../../../../components/Dialog";
import { findObjectUseCase } from "../../../../../usecases/object";
import { auditLogUseCase } from "../../../../../usecases/logs";
import getChangedFields from "../../../../../getChangedFields";

class CategoryFormPresenter extends BaseFormPresenter {
  computePartialCost(usedQty, stockQty, stockCost) {
    if (
      !isNaN(usedQty) &&
      !isNaN(stockQty) &&
      stockQty > 0 &&
      !isNaN(stockCost)
    ) {
      return ((usedQty / stockQty) * stockCost).toFixed(2);
    }
    return "0.00";
  }

  async updateComponentRawMaterialUsage(rawMaterialId) {
    const affectedComponents = await findObjectUseCase().execute("components", {
      where: { "raw_materials.id": rawMaterialId },
    });

    for (const component of affectedComponents) {
      const updatedRawMaterials = component.raw_materials.map((raw) => {
        const isMatched = raw.id === rawMaterialId;
        const cost = isMatched
          ? this.change.cost || this.object.cost
          : raw.cost;
        const stockQty = isMatched
          ? this.change.stock_quantity || this.object.stock_quantity
          : raw.stock_quantity;

        const partial_cost = this.computePartialCost(
          parseFloat(raw.quantity),
          parseFloat(stockQty),
          parseFloat(cost)
        );

        return {
          ...raw,
          cost,
          stock_quantity: stockQty,
          partial_cost,
        };
      });

      const totalComponentCost = updatedRawMaterials
        .reduce((acc, raw) => acc + parseFloat(raw.partial_cost || "0"), 0)
        .toFixed(2);

      const updatedComponent = {
        ...component,
        raw_materials: updatedRawMaterials,
        cost: totalComponentCost,
      };

      await this.upsertUseCase.execute("components", updatedComponent);

      // Find all products that use this updated component
      const affectedProducts = await findObjectUseCase().execute("products", {
        where: { "components.id": component.id },
      });

      for (const product of affectedProducts) {
        // Update matching component inside the product
        const updatedProductComponents = product.components.map((comp) => {
          if (comp.id === component.id) {
            return updatedComponent;
          }
          return comp;
        });

        // Optionally update raw_materials as well if raw used directly
        const updatedProductRawMaterials = product.raw_materials.map((raw) => {
          const isMatched = raw.id === rawMaterialId;
          const cost = isMatched
            ? this.change.cost || this.object.cost
            : raw.cost;
          const stockQty = isMatched
            ? this.change.stock_quantity || this.object.stock_quantity
            : raw.stock_quantity;

          const partial_cost = this.computePartialCost(
            parseFloat(raw.quantity),
            parseFloat(stockQty),
            parseFloat(cost)
          );

          return {
            ...raw,
            cost,
            stock_quantity: stockQty,
            partial_cost,
          };
        });

        // Recompute total product cost (raws + component costs)
        const rawCost = updatedProductRawMaterials.reduce(
          (sum, raw) => sum + parseFloat(raw.partial_cost || "0"),
          0
        );

        const componentCost = updatedProductComponents.reduce(
          (sum, comp) => sum + parseFloat(comp.cost || "0"),
          0
        );

        // const totalProductCost = (rawCost + componentCost).toFixed(2);
        const totalProductCost = rawCost.toFixed(2);

        const updatedProduct = {
          ...product,
          raw_materials: updatedProductRawMaterials,
          components: updatedProductComponents,
          cost: totalProductCost,
        };

        await this.upsertUseCase.execute("products", updatedProduct);
      }
    }
  }

  async save() {
    const collection = this.view.getCollectionName();
    const user = this.view.getCurrentUser();
    const fullAfter = { ...this.object, ...this.change };
    const changedFields = getChangedFields(this.object || {}, fullAfter);

    const isNew = !this.object?.id;
    const changedKeys = Object.keys(this.change).filter((k) => k !== "id");
    const summary = changedKeys
      .map((key) => `${key}: ${this.change[key]}`)
      .join(", ");

    const message = isNew
      ? `Created new Raw Material: ${this.change.itemName}`
      : changedFields.length === 0
      ? `Updated Raw Material: ${this.object.itemName} — changed: ${summary}`
      : `Updated Raw Material: ${this.object.itemName} — changed: ${summary}`;

    if (this.object.id) {
      this.change.id = this.object?.id;
    } else {
      this.change.acl = this.view.getAcl();
    }
    // this.change.storeId = { id: user.storeAnalytic[0]?.id };
    console.log("user", user);

    try {
      const result = await this.upsertUseCase.execute(collection, this.change);
      console.log("payload", result);
      await auditLogUseCase()[isNew ? "create" : "update"]({
        user,
        logDate: new Date().toISOString(),
        message,
        collection,
        objectId: result?.id,
        payload: result,
        store: { id: result.storeId?.id },
      });

      if (this.change.id) {
        await this.updateComponentRawMaterialUsage(this.change.id);
      }

      this.change = {};
    } catch (error) {
      // throw error; // rethrow the error to be caught by the caller
    }
  }

  async submit() {
    if (Object.values(this.change).length === 0) {
      this.view.showSuccessSnackbar("Successfully saved!");
      return;
    }
    try {
      this.view.submitting();
      dialog.close();
      await this.save();

      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Saved Successfully"
            message=""
            isCancelButton={false}
            onSubmit={() => {
              this.view.navigateBack();
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }
}

export default CategoryFormPresenter;
